/**
 * What a touch does to the ball.
 *
 * The reachability search decided WHICH clip and WHEN. This decides what actually happens on
 * the frame the foot arrives, and it is where dribbling gets its character.
 *
 * The interesting one is the dribble touch. It does not pick a force and apply it. It works
 * out where the ball SHOULD be by the time the player wants to touch it again — a moment
 * that gets further away the faster he is running, from a quarter of a second at a walk to
 * nearly a second at a sprint — and then solves for the push that puts the ball there. That
 * is why a sprinting player knocks the ball far ahead and a walking player keeps it under
 * his feet, without either being written down anywhere.
 *
 * A trap is the same calculation with the ball's own pace and a difficulty penalty layered
 * on top: a hard, bouncing ball arriving while the player is being jostled comes off his
 * foot further, higher, and with more of its original direction left in it.
 *
 * Source: humanoid_utils.cpp:146-352.
 */

import { Vector3 } from 'three';
import { PITCH } from '../../core/constants';
import {
  angle2D,
  clamp,
  curve,
  normalizedClamp,
  normalizedOr,
  normalizedOrZero,
  rotate2D,
  signSide,
  vecLength,
} from '../../core/math';
import { stretchSprintTo } from './physics';
import {
  DRIBBLE_VELOCITY,
  DRIBBLE_WALK_SWITCH,
  IDLE_VELOCITY,
  SPRINT_VELOCITY,
  WALK_SPRINT_SWITCH,
  WALK_VELOCITY,
  Velocity,
  floatToEnumVelocity,
} from './velocity';
import { frontOfFootOffsetRel } from './touch';
import type { SpatialState } from './spatial';

const FORWARD = new Vector3(0, -1, 0);
/** gamedefines.hpp:64 */
const DEFAULT_TOUCH_OFFSET_MS = 80;

/** The spin a touch imparts, in radians per second about each axis. */
export interface TouchSpin {
  x: number;
  y: number;
  z: number;
}

export interface TouchContext {
  /** Where the ball is and how it is moving, right now. */
  ballPosition: Vector3;
  ballMovement: Vector3;
  /** Where the player will be, and which way he will face, when this clip ends. */
  nextStartPos: Vector3;
  nextStartAngle: number;
  /** Which way his torso will be pointing then — including the rotation smuggle. */
  nextBodyAngle: number;
  /** The solved end-of-clip movement, world space. */
  outgoingMovement: Vector3;
  spatial: SpatialState;
  /** How far collisions have shoved him off his animated path. */
  positionOffset: Vector3;

  /** The clip that is playing, and the wish it was chosen for. */
  clipOutgoingVelocity: number;
  effectiveFrameCount: number;
  frameNum: number;
  desiredDirection: Vector3;
  desiredVelocityFloat: number;
  /** What the controller wants RIGHT NOW, which may have moved on since the clip began. */
  controllerVelocityFloat: number;

  hasPossession: boolean;
  maxVelocity: number;
  stat: (name: string) => number;
  closestOpponentDistance: number;
}

/**
 * The dribble touch — `GetBallControlVector` (humanoid_utils.cpp:208-332).
 *
 * Writes the spin it imparts into `spin`.
 */
export function ballControlVector(
  context: TouchContext,
  ffoOffset: number,
  spin: TouchSpin,
  out = new Vector3(),
): Vector3 {
  // How much of the result is "what the physics say" versus "what the player asked for".
  // A player who does not have the ball under control gets almost no say; a player whose
  // clip ends standing still gets none at all, because there is no movement to express.
  let physicsBias = 0.7;
  if (!context.hasPossession) physicsBias = 0.9;
  const outgoingIsIdle =
    floatToEnumVelocity(context.clipOutgoingVelocity) === Velocity.Idle;
  if (outgoingIsIdle) physicsBias = 1.0;

  // How much faster than the physics allow he may knock it, as an act of intent. A player
  // asking for a sprint gets more of this than one asking for a walk, and none of it if he
  // is turning away from the direction he asked for.
  const explosiveness = 0.6;
  let maximumOverdrive = 1.0 * explosiveness;
  if (context.desiredVelocityFloat < DRIBBLE_WALK_SWITCH) maximumOverdrive = 0;
  if (context.desiredVelocityFloat > WALK_SPRINT_SWITCH) maximumOverdrive = 2.0 * explosiveness;
  const dotFactor1 = context.spatial.directionVec.dot(context.desiredDirection) * 0.5 + 0.5;
  const dotFactor2 =
    rotate2D(FORWARD, context.nextStartAngle, _scratch).dot(context.desiredDirection) * 0.5 + 0.5;
  maximumOverdrive *= Math.min(dotFactor1, dotFactor2);
  if (outgoingIsIdle) maximumOverdrive = 0;

  const originatingBias = 0.7;
  const desiredMovement = _desiredMovement
    .copy(context.desiredDirection)
    .multiplyScalar(
      context.desiredVelocityFloat * originatingBias +
        context.controllerVelocityFloat * (1 - originatingBias),
    );

  // A slow player must not kick the ball as far as a fast one would.
  const desiredSpeed = vecLength(desiredMovement);
  if (desiredSpeed > WALK_SPRINT_SWITCH) {
    const stretched = stretchSprintTo(desiredSpeed, SPRINT_VELOCITY, context.maxVelocity);
    normalizedOrZero(desiredMovement, desiredMovement).multiplyScalar(stretched);
  }

  // Never below what the physics will do — he would run over the ball — and never much
  // above it either, or the ball ends up out of reach.
  const outgoingSpeed = vecLength(context.outgoingMovement);
  const velocity = clamp(vecLength(desiredMovement), outgoingSpeed, outgoingSpeed + maximumOverdrive);
  normalizedOr(desiredMovement, rotate2D(FORWARD, context.nextStartAngle, _scratch), desiredMovement)
    .multiplyScalar(velocity);
  const physicsMovement = rotate2D(FORWARD, context.nextStartAngle, _physicsMovement)
    .multiplyScalar(velocity);

  const desiredVelocity = vecLength(desiredMovement);
  const physicsVelocity = vecLength(physicsMovement);

  // Where the ball wants to sit relative to him, pushed further out when an opponent is
  // breathing down his neck — a calm, balanced player keeps it closer under pressure.
  const ffoSrc = frontOfFootOffsetRel(
    physicsVelocity,
    context.nextBodyAngle - context.spatial.angle,
    context.ballPosition.z,
    _ffoSrc,
  );
  const annoyanceVeloFactor = curve(
    normalizedClamp(context.clipOutgoingVelocity, IDLE_VELOCITY, SPRINT_VELOCITY),
    0.7,
  );
  const opponentAnnoyance =
    (1 - normalizedClamp(context.closestOpponentDistance, 0.5, 1.5)) *
    (1 - (context.stat('mental_calmness') * 0.5 + context.stat('physical_balance') * 0.3)) *
    annoyanceVeloFactor;
  const ffo = rotate2D(FORWARD, context.nextBodyAngle, _ffo).multiplyScalar(
    vecLength(ffoSrc) + ffoOffset + opponentAnnoyance * 3.0,
  );
  // A high ball is bounced off the body rather than kept at the feet, or it keeps
  // colliding with him.
  const heightFfoOffset = normalizedClamp(context.ballPosition.z, 0.5, 1.0) * 0.5;
  ffo.addScaledVector(ffoSrc, heightFfoOffset * 0.5);
  ffo.addScaledVector(context.spatial.bodyDirectionVec, heightFfoOffset * 0.5);

  // How long from now until he wants to be able to hit it again. This is the number that
  // makes a sprinting dribble long and a walking one short.
  const desiredDelayTime =
    Math.pow(normalizedClamp(desiredVelocity, 0, SPRINT_VELOCITY), 2) * 0.6 + 0.25;
  const physicsDelayTime =
    Math.pow(normalizedClamp(physicsVelocity, 0, SPRINT_VELOCITY), 2) * 0.6 + 0.25;

  const desiredPlannedBallPos = _desiredPlanned
    .copy(context.nextStartPos)
    .addScaledVector(desiredMovement, desiredDelayTime)
    .add(ffo);
  const physicsPlannedBallPos = _physicsPlanned
    .copy(context.nextStartPos)
    .addScaledVector(physicsMovement, physicsDelayTime)
    .add(ffo);

  const plannedBallPos = _planned
    .copy(physicsPlannedBallPos)
    .multiplyScalar(physicsBias)
    .addScaledVector(desiredPlannedBallPos, 1 - physicsBias);
  const toPlannedBall = _toPlanned
    .set(plannedBallPos.x, plannedBallPos.y, 0)
    .sub(_scratch.set(context.ballPosition.x, context.ballPosition.y, 0));

  let timeToGo = (context.effectiveFrameCount - context.frameNum) * 10 * 0.001;
  timeToGo += physicsDelayTime * physicsBias + desiredDelayTime * (1 - physicsBias);
  timeToGo += DEFAULT_TOUCH_OFFSET_MS * 0.001;

  // Solve for the push. The exponent is there because the ball loses speed faster the
  // faster it is going, so distance is not linear in the force applied.
  let divisor = timeToGo * (0.38 + 0.02 * context.stat('technical_dribble'));
  divisor *= 1.1;
  const power = Math.pow(vecLength(toPlannedBall) / divisor, 0.7);
  const direction = normalizedOr(
    toPlannedBall,
    rotate2D(FORWARD, context.nextBodyAngle, _scratch),
    _direction,
  );

  const height = clamp(0.1 + 1.5 * Math.pow(power / 10, 1.6), 0, 1.5);

  let powerMultiplier = 1.2 - context.stat('technical_ballcontrol') * 0.03;
  const veloBias = normalizedClamp(velocity, WALK_VELOCITY, SPRINT_VELOCITY - 0.8);
  powerMultiplier = 1.0 * (1 - veloBias) + powerMultiplier * veloBias;

  out.copy(direction).multiplyScalar(power * powerMultiplier);
  out.z += height;

  // Backspin on a gentle touch, topspin on a firm one.
  const backspinFactor = 30.0;
  const velo = vecLength(out);
  const veloFactor = Math.pow(normalizedClamp(velo, 0, 10), 0.7);
  const spinAxis = normalizedOrZero(_scratch.copy(ffo), _scratch)
    .multiplyScalar(0.6)
    .addScaledVector(normalizedOrZero(out, _spinScratch), 0.4);
  normalizedOrZero(spinAxis, spinAxis);
  const spinAmount = 2 * Math.PI * velo - backspinFactor * veloFactor;
  spin.x = spinAxis.y * spinAmount;
  spin.y = spinAxis.x * spinAmount;

  return out;
}

/**
 * How badly a control touch is likely to go wrong — `GetDifficultyFactors`
 * (humanoid_utils.cpp:146-206).
 *
 * Three separate penalties, because a touch can fail in three ways: the ball comes off too
 * far, it comes off too high, or it keeps too much of where it was already going. A fast
 * ball, a ball far from the body, and being physically shoved all make it worse; ball
 * control skill halves it, and there is a genuine dice roll on top.
 */
export function difficultyFactors(
  context: TouchContext,
  interceptingOpponentTouchBias: number,
  random: () => number,
): { distance: number; height: number; ballMovement: number } {
  const positionOffsetPenalty = normalizedClamp(vecLength(context.positionOffset), 0, 0.1) * 2;
  const ballBodyVeloPenalty =
    Math.pow(
      normalizedClamp(
        vecLength(_scratch.copy(context.spatial.movement).sub(context.ballMovement)),
        10,
        50,
      ),
      1.5,
    ) * 5;
  const fartherAwayPenalty =
    Math.pow(
      normalizedClamp(
        _scratch
          .copy(context.spatial.position)
          .addScaledVector(context.spatial.directionVec, 0.2)
          .distanceTo(_flat.set(context.ballPosition.x, context.ballPosition.y, 0)),
        0.7,
        1.3,
      ),
      2,
    ) * 2;

  let distance = positionOffsetPenalty * 2 + ballBodyVeloPenalty + fartherAwayPenalty * 4;
  let height = positionOffsetPenalty * 0.5 + ballBodyVeloPenalty * 2 + fartherAwayPenalty;
  let ballMovement = 0;

  // Intercepting somebody else's pass is harder than collecting your own team's.
  if (interceptingOpponentTouchBias > 0) {
    const penalty = Math.pow(interceptingOpponentTouchBias, 0.6) * 5;
    distance += penalty;
    height += penalty;
    ballMovement += penalty * 0.1;
  }
  ballMovement = clamp(ballMovement, 0, 0.9);

  const skillMultiplier =
    (1 - context.stat('technical_ballcontrol') * 0.5) * (0.5 + random() * 0.5);
  return {
    distance: clamp(distance * skillMultiplier, 0, 1),
    height: clamp(height * skillMultiplier, 0, 1),
    ballMovement: clamp(ballMovement * skillMultiplier, 0, 1),
  };
}

/**
 * The trap — `GetTrapVector` (humanoid_utils.cpp:334-352).
 *
 * A dribble touch with the difficulty penalties folded in: the "how far" penalty is fed
 * back in as extra distance in front of the foot, the "how high" penalty lifts the ball off
 * his boot, and the "how much of its own movement survives" penalty blends the ball's
 * existing flight back in. A perfectly executed trap is exactly a dribble touch.
 */
export function trapVector(
  context: TouchContext,
  interceptingOpponentTouchBias: number,
  random: () => number,
  spin: TouchSpin,
  out = new Vector3(),
): Vector3 {
  const factors = difficultyFactors(context, interceptingOpponentTouchBias, random);
  ballControlVector(context, factors.distance, spin, out);
  out.z += context.ballMovement.z * factors.height + factors.height * 4;
  return out
    .multiplyScalar(1 - factors.ballMovement)
    .addScaledVector(context.ballMovement, factors.ballMovement);
}

/** `Humanoid::GetBestPossibleTouch` (humanoid.cpp:2410-2486). */
export function bestPossibleTouch(
  context: TouchContext,
  functionType: 'shortpass' | 'longpass' | 'highpass',
  desiredTouch: Vector3,
  touchMaxPowerFactor: number,
  touchDifficultyFactor: number,
  animBallDirection: Vector3,
  interceptingOpponentTouchBias: number,
  random: () => number,
  out = new Vector3(),
): Vector3 {
  const maxPowerBase = functionType === 'highpass' ? 42 : 30;
  const maxPowerFactor = (touchMaxPowerFactor || 1) * 0.7 + 0.3;
  let maxPower =
    maxPowerBase *
    maxPowerFactor *
    (1 - clamp(vecLength(context.positionOffset) * 2.5, 0, 0.25));
  maxPower += vecLength(context.ballMovement) * 0.5;

  out.copy(desiredTouch);
  const desiredPower = vecLength(out);
  if (desiredPower > maxPower) {
    const missingPower = desiredPower - maxPower;
    normalizedOrZero(out, out).multiplyScalar(maxPower);
    out.z += clamp(missingPower, 0, 10) * 0.25;
  }

  let difficulty = touchDifficultyFactor;
  if (functionType === 'shortpass' || functionType === 'longpass') {
    difficulty *= 1 - context.stat('technical_shortpass') * 0.5;
  } else {
    difficulty *= 1 - context.stat('technical_highpass') * 0.5;
  }

  const factors = difficultyFactors(context, interceptingOpponentTouchBias, random);
  const randomRotation =
    factors.distance * 0.15 +
    factors.height * 0.15 +
    factors.ballMovement * 0.3 +
    difficulty * 0.5;

  if (vecLength(animBallDirection) > 0.01) {
    const bias = clamp(randomRotation * 1.5, 0, 1);
    const height = out.z;
    const length = vecLength(out);
    _nativeTouch.copy(animBallDirection).setZ(0);
    normalizedOr(_nativeTouch, out, _nativeTouch).multiplyScalar(length);
    _nativeTouch.z = height;
    out.multiplyScalar(1 - bias).addScaledVector(_nativeTouch, bias);
  } else {
    rotate2D(
      out,
      (random() - 0.5) * Math.PI * Math.min(randomRotation, 0.5),
      out,
    );
  }

  out.multiplyScalar(1 - factors.distance * 0.3);
  out.z += factors.distance * 1.5;
  out.z += context.ballMovement.z * factors.height * 0.5 + factors.height;
  out.multiplyScalar(1 - factors.ballMovement).addScaledVector(
    context.ballMovement,
    factors.ballMovement,
  );
  out.z += difficulty * 5 * (0.2 + random() * 0.8);
  return out;
}

/**
 * How hard a pass is asked for, as the contact reads it: the ball leaves the boot at
 * `SCALE x (power + OFFSET)` metres a second before difficulty and the clip's own direction get
 * at it (`humanoid.cpp:509`).
 */
const PASS_POWER_SCALE = 36;
const PASS_POWER_OFFSET = 0.3;

/**
 * The same curve read backwards: what to ask for so the ball leaves at `speed`.
 *
 * Every other strike in the game charges its power on a key or reads it off a distance. A chip
 * is the one that has to be solved — its whole point is landing just past the goal line — so it
 * needs the inverse.
 *
 * NOTHING SLOWER THAN 10.8 M/S CAN BE ASKED FOR: a power of zero is already `36 x 0.3`. That is
 * a floor rather than a fault. A chip that slow carries about nine metres, and from nine metres
 * out there is no keeper position a chip beats anyway — he is close enough to his line to reach
 * everything that is still under his bar.
 */
export function passPowerForSpeed(speed: number): number {
  return clamp(speed / PASS_POWER_SCALE - PASS_POWER_OFFSET, 0, 1);
}

/** Pass contact and spin — humanoid.cpp:463-546. */
export function passVector(
  context: TouchContext,
  functionType: 'shortpass' | 'longpass' | 'highpass',
  desiredDirection: Vector3,
  desiredPower: number,
  touchMaxPowerFactor: number,
  touchDifficultyFactor: number,
  animBallDirection: Vector3,
  interceptingOpponentTouchBias: number,
  random: () => number,
  spin: TouchSpin,
  out = new Vector3(),
): Vector3 {
  normalizedOr(desiredDirection, context.spatial.directionVec, out).multiplyScalar(
    PASS_POWER_SCALE * (desiredPower + PASS_POWER_OFFSET),
  );
  bestPossibleTouch(
    context,
    functionType,
    out,
    touchMaxPowerFactor,
    touchDifficultyFactor,
    animBallDirection,
    interceptingOpponentTouchBias,
    random,
    out,
  );

  let bodyTouchAngle = angle2D(context.spatial.bodyDirectionVec, out) / Math.PI;
  if (Math.abs(bodyTouchAngle) > 0.5) {
    bodyTouchAngle = (1 - Math.abs(bodyTouchAngle)) * signSide(bodyTouchAngle);
  }
  bodyTouchAngle *= 2;
  let amount = bodyTouchAngle * 0.25;
  if (functionType === 'highpass') amount *= 0.2;
  rotate2D(
    out,
    amount * (0.4 + 0.6 * normalizedClamp(vecLength(out), 0, 70)),
    out,
  );

  const forwardness = functionType === 'highpass' ? -1.3 : 3.5;
  const spinPower = clamp(vecLength(out), 0, 15) * forwardness;
  normalizedOrZero(out, _spinScratch);
  spin.x = _spinScratch.y * spinPower;
  spin.y = _spinScratch.x * spinPower;
  spin.z = amount * -340;
  return out;
}

/**
 * The most a shot may be re-aimed on the frame it is struck — eighteen degrees,
 * `humanoid.cpp:556`. Enough to undo where a running player has got to since he decided to
 * shoot; not enough to turn one shot into a different shot.
 */
export const MAX_SHOT_REAIM_ANGLE = 0.1 * Math.PI;

/**
 * Below this, the shot runs so nearly parallel to the goal line that the point where it
 * crosses is arithmetic noise, and re-aiming at that point is meaningless.
 */
const MIN_GOALWARD_COMPONENT = 0.1;

/**
 * Point a committed shot at the spot it was meant for, from where the ball actually is.
 *
 * A player commits to a shot about a fifth of a second before his boot arrives, and the
 * direction is settled then, from where he was standing at that moment. By the time the
 * contact happens he has run on and the ball is sitting off to one side of his feet rather
 * than under his middle, so firing along the old direction from the new place sends the ball
 * past the spot he picked, by however far sideways the two of them have moved.
 *
 * The fix is a straight line problem. The committed direction, run forward from where it was
 * measured, crosses the goal line somewhere; that crossing point is what the shooter chose.
 * Recover it, and aim at it from where the ball is now.
 *
 * It is a small correction and it is meant to be. Measured over 378 real shots, eight
 * centimetres at the goal line on average and twenty-seven at worst, because the aim is
 * already worked out from where the player will be a fifth of a second later, which is most
 * of the way to the contact frame. A shot's own scatter over the same range is twenty-four
 * centimetres from six metres out and over a metre from twenty-four, so this cannot move how
 * often shots go in — it just stops a striker's own stride from nudging the ball off target.
 *
 * The original computes a value on this frame too and then never uses it: it works one out at
 * `humanoid.cpp:550-566` and hands it nowhere, because `GetShotVector` reaches back into the
 * originating command for the raw direction instead (`humanoid_utils.cpp:371-377,455`). Its
 * value is not a refinement of the aim at all — it re-runs the aiming assist a person's
 * joystick goes through, which is right for a person and wrong for everyone else. A computer
 * player never fills in a joystick direction, so that assist collapses to the dead centre of
 * the goal: over the same 378 shots it drags the ball from 2.75 m off centre to 0.22 m, which
 * is every shot in an unattended match struck straight at the goalkeeper. What is done here
 * instead keeps the corner the shooter picked and corrects only what has actually gone stale,
 * which is where he is shooting from.
 *
 * The correction is capped at the original's eighteen degrees, so a ball hoofed clear under
 * pressure — never aimed at the goal in the first place — stays roughly where it was hit.
 *
 * `side` says which end this player's own team defends: the goal he is shooting at is the
 * other one.
 */
export function refinedShotDirection(
  committedDirection: Vector3,
  aimOrigin: Vector3,
  contactPosition: Vector3,
  side: -1 | 1,
  out = new Vector3(),
): Vector3 {
  const committed = normalizedOrZero(
    _committed.set(committedDirection.x, committedDirection.y, 0),
    _committed,
  );
  if (committed.x === 0 && committed.y === 0) return out.copy(committedDirection);
  // Struck away from the goal he is attacking — a clearance, or a shot that was already
  // going nowhere near. There is no crossing point to re-aim at, so leave it alone.
  if (committed.x * -side < MIN_GOALWARD_COMPONENT) return out.copy(committed);

  const goalLineX = -side * PITCH.halfLength;
  const range = (goalLineX - aimOrigin.x) / committed.x;
  // Already level with the goal line or past it: nothing in front to aim at.
  if (!(range > 0)) return out.copy(committed);

  const aimed = _reaim.set(goalLineX, aimOrigin.y + committed.y * range, 0);
  const refined = normalizedOrZero(
    aimed.sub(_contactFlat.set(contactPosition.x, contactPosition.y, 0)),
    _reaim,
  );
  if (refined.x === 0 && refined.y === 0) return out.copy(committed);

  const angleDiff = angle2D(refined, committed);
  if (Math.abs(angleDiff) > MAX_SHOT_REAIM_ANGLE) {
    return rotate2D(
      committed,
      clamp(angleDiff, -MAX_SHOT_REAIM_ANGLE, MAX_SHOT_REAIM_ANGLE),
      out,
    );
  }
  return out.copy(refined);
}

/**
 * Shot contact and spin — `GetShotVector` (humanoid_utils.cpp:354-510).
 *
 * `confidenceBonus` is not part of the original: it is how a human-only "nobody is marking me
 * and I have had a full second to plant my foot" bonus reaches this formula, rather than
 * inventing a second, parallel notion of accuracy next to the one already computed here. 0
 * (absent) reproduces the original exactly, which is every shot the computer ever takes and
 * every ordinary human one. See `SHOT_BONUS_CONFIDENCE` in `src/sim/controller.ts` for where a
 * nonzero value comes from and why that number was chosen.
 */
export function shotVector(
  context: TouchContext,
  touchMovement: Vector3,
  desiredDirection: Vector3,
  desiredPower: number,
  touchMaxPowerFactor: number,
  random: () => number,
  spin: TouchSpin,
  out = new Vector3(),
  confidenceBonus = 0,
): Vector3 {
  const touchVelocity = vecLength(touchMovement);
  const touchDirection =
    floatToEnumVelocity(touchVelocity) === Velocity.Idle
      ? _touchDirection.copy(context.spatial.directionVec)
      : normalizedOr(touchMovement, context.spatial.directionVec, _touchDirection);
  const desired = normalizedOr(
    _flatDesired.set(desiredDirection.x, desiredDirection.y, 0),
    context.spatial.directionVec,
    _flatDesired,
  );

  const idealAngle = 0.2 * Math.PI;
  const angle1 = Math.abs(angle2D(touchDirection, rotate2D(desired, -idealAngle, _angleDirection)));
  const angle2 = Math.abs(angle2D(touchDirection, rotate2D(desired, idealAngle, _angleDirectionB)));
  const directionFactor = curve(1 - clamp(Math.min(angle1, angle2) / Math.PI, 0, 1), 0.8);

  const directionPower = Math.pow(directionFactor, 0.8);
  const velocityPower = Math.pow(normalizedClamp(touchVelocity, 0, SPRINT_VELOCITY), 0.3);
  const offsetPower = 1 - normalizedClamp(vecLength(context.positionOffset), 0, 0.1);
  const powerFactor = clamp(
    (0.7 + directionPower * 0.3) *
      (0.7 + velocityPower * 0.3) *
      (0.4 + offsetPower * 0.6),
    0,
    1,
  );
  const adaptedDesiredPower = 45 * (0.7 + Math.pow(desiredPower, 0.5) * 0.3);
  const animMaxPowerFactor = touchMaxPowerFactor || 1;
  let power = clamp(
    powerFactor * adaptedDesiredPower,
    0,
    (32 + context.stat('physical_shotpower') * 13) *
      (0.2 + animMaxPowerFactor * 0.8) *
      (1 + confidenceBonus),
  );
  const relativeBallMovement = vecLength(_relativeBall.copy(touchMovement).sub(context.ballMovement));
  const relativePowerFactor = normalizedClamp(relativeBallMovement, 0, 10);
  power *= 1 + relativePowerFactor * 0.2;

  const velocityEase = 1 - normalizedClamp(Math.abs(touchVelocity - DRIBBLE_VELOCITY), 0, 4);
  const movementEase = 1 - relativePowerFactor * (0.5 - context.stat('technical_volley') * 0.3);
  const powerEase = curve(1 - normalizedClamp(power, 30, 100), 1);
  const easiness =
    (0.4 + Math.pow(directionFactor, 0.8) * 0.6) *
    (0.7 + velocityEase * 0.3) *
    (0.3 + offsetPower * 0.7) *
    (0.6 + movementEase * 0.4) *
    (0.6 + powerEase * 0.4);
  // The confidence bonus shaves a fraction off whatever difficulty this strike already has,
  // rather than setting its own floor — a shot that was already easy has little left to cut,
  // which is right: nobody gets more accurate than clean contact with a stationary target.
  const difficulty = clamp((1 - easiness) * (1 - confidenceBonus), 0, 1);

  _desiredShot.copy(desired).setZ(0.05);
  normalizedOrZero(_desiredShot, _desiredShot).multiplyScalar(power);

  const lag = difficulty * 0.8;
  _worstDirection.copy(touchDirection).multiplyScalar(lag).addScaledVector(desired, 1 - lag);
  normalizedOrZero(_worstDirection, _worstDirection);
  _worstDirection.add(
    _randomDirection.set(random() * 2 - 1, random() * 2 - 1, random() * 2 - 1)
      .multiplyScalar(0.5 * difficulty),
  );
  normalizedOrZero(_worstDirection, _worstDirection);
  const worstHeight = curve(Math.pow(difficulty, 0.7), 0.7) * 0.7;
  const worstPower = power * (1 - Math.pow(difficulty, 0.7) * 0.5);
  _worstShot.copy(_worstDirection).add(_shotHeight.set(0, 0, worstHeight));
  normalizedOrZero(_worstShot, _worstShot).multiplyScalar(worstPower);

  const worstFactor = Math.pow(random(), context.stat('technical_shot') * 0.7);
  out.copy(_desiredShot).multiplyScalar(1 - worstFactor).addScaledVector(_worstShot, worstFactor);

  const randomCurveFactor = 0.3 + worstFactor * 0.7;
  spin.x = -desired.y * 20 + (random() * 40 - 20) * randomCurveFactor;
  spin.y = -desired.x * 20 + (random() * 40 - 20) * randomCurveFactor;

  let bodyTouchAngle = angle2D(context.spatial.bodyDirectionVec, out) / Math.PI;
  if (Math.abs(bodyTouchAngle) > 0.5) {
    bodyTouchAngle = (1 - Math.abs(bodyTouchAngle)) * signSide(bodyTouchAngle);
  }
  bodyTouchAngle *= 2;
  const amount = bodyTouchAngle * 0.25;
  rotate2D(
    out,
    amount * (0.4 + 0.6 * normalizedClamp(vecLength(out), 0, 70)),
    out,
  );
  spin.z = amount * -420 + (random() * 40 - 20) * 0.7;
  return out;
}

/** Standing tackle scramble — humanoid.cpp:584-596. */
export function interfereVector(
  context: TouchContext,
  interceptingOpponentTouchBias: number,
  random: () => number,
  spin: TouchSpin,
  out = new Vector3(),
): Vector3 {
  trapVector(context, interceptingOpponentTouchBias, random, spin, out).multiplyScalar(0.5);
  normalizedOrZero(
    _toBall
      .copy(context.ballPosition)
      .setZ(0)
      .sub(_player2D.copy(context.spatial.position).setZ(0)),
    _toBall,
  );
  out.addScaledVector(_toBall, 4);
  out.z += 0.5 + random();
  return out;
}

/**
 * How much of the ball's own line a fingertip save leaves it with. A punch keeps none of it —
 * the original reverses a tenth — so a save has to be able to sit somewhere between the two.
 */
const TIP_CARRY = 0.35;
/**
 * The shove ACROSS the ball, against the 4 the punch pushes up the pitch. It has to be large:
 * a goal is 7.3 m wide, so a ball tipped from near the middle of it has three and a half
 * metres to travel sideways before it is past a post, and it only has the last metre or two of
 * its flight to do that in. At 8 it cleared the frame from the sides and not from the middle,
 * and the balls it failed to clear went in — conversion rose from 8.0% to 13.5% against real
 * football's 11%. See PENDING.md for the before and after either side of this number.
 */
const TIP_ACROSS = 14;
/** And the same idea upwards, for the ones that go over the bar. */
const TIP_LIFT = 4;

/**
 * Goalkeeper parry when the ball is too hard to retain — humanoid.cpp:598-630, and then a
 * departure from it.
 *
 * The original treats every save he cannot hold as a PUNCH: it throws away the ball's own
 * direction and substitutes a fixed shove away from his goal. That reads as a keeper who is
 * always perfectly set, always facing the shot, always able to choose where the ball goes —
 * and it has a consequence nobody would design on purpose, which is that the push points up
 * the pitch by construction, so **a goalkeeper can never put a shot behind for a corner.**
 * Measured: not one of them ever did.
 *
 * A keeper at full stretch is not punching anything. He gets a hand across a ball he cannot
 * stop and turns it, and it carries on past him — outside the post, or over the bar. So the
 * ball's own line survives in proportion to how nearly he could have held it, and what he adds
 * is a shove ACROSS that line, away from the middle of his goal, rather than back down it.
 *
 * The three numbers this needs are chosen rather than derived, and the argument for them is
 * only that they produce the football; measure corners and conversion together around any
 * change to them.
 */
export function deflectVector(
  ballMovement: Vector3,
  playerMovement: Vector3,
  teamSide: -1 | 1,
  /** 0 when he only got fingertips to it, 1 when he could nearly have caught it. */
  control: number,
  /** How far off the middle of his goal the ball is. Decides which way he turns it. */
  ballOffset: number,
  random: () => number,
  out = new Vector3(),
): Vector3 {
  const held = clamp(control, 0, 1);
  const stretch = 1 - held;
  _flatBall.copy(ballMovement).setZ(0);
  // Dead centre he has no near post to aim for, so he follows his own dive out.
  const across = ballOffset !== 0 ? Math.sign(ballOffset) : Math.sign(playerMovement.y) || 1;
  out
    .copy(_flatBall)
    .multiplyScalar(-0.1 * held + TIP_CARRY * stretch)
    .addScaledVector(playerMovement, 2)
    .add(_goalward.set(-teamSide * 4 * held, across * TIP_ACROSS * stretch, 0));
  out.y += random() * 2 - 1;
  const power = vecLength(_flatBall) * 0.3 + vecLength(playerMovement) * 2.5;
  normalizedOrZero(out, out).multiplyScalar(power);
  out.z += 1.2 + TIP_LIFT * stretch;
  return out;
}

/** Sliding-tackle contact — humanoid.cpp:632-643. */
export function slidingVector(
  animBallDirection: Vector3,
  playerAngle: number,
  ballMovement: Vector3,
  out = new Vector3(),
): Vector3 {
  rotate2D(animBallDirection, playerAngle, out)
    .multiplyScalar(6)
    .addScaledVector(ballMovement, -0.28);
  out.z += 6;
  return out;
}

const _scratch = new Vector3();
const _spinScratch = new Vector3();
const _flat = new Vector3();
const _desiredMovement = new Vector3();
const _physicsMovement = new Vector3();
const _ffoSrc = new Vector3();
const _ffo = new Vector3();
const _desiredPlanned = new Vector3();
const _physicsPlanned = new Vector3();
const _planned = new Vector3();
const _toPlanned = new Vector3();
const _direction = new Vector3();
const _nativeTouch = new Vector3();
const _touchDirection = new Vector3();
const _committed = new Vector3();
const _reaim = new Vector3();
const _contactFlat = new Vector3();
const _flatDesired = new Vector3();
const _angleDirection = new Vector3();
const _angleDirectionB = new Vector3();
const _relativeBall = new Vector3();
const _desiredShot = new Vector3();
const _worstDirection = new Vector3();
const _randomDirection = new Vector3();
const _worstShot = new Vector3();
const _shotHeight = new Vector3();
const _toBall = new Vector3();
const _player2D = new Vector3();
const _flatBall = new Vector3();
const _goalward = new Vector3();
